import React, { useEffect, useId, useState } from 'react';
import { Call, Category, CallInput, AccountMatch, createCall, updateCall, lookupAccount, lookupByPhone } from '../api';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { ui } from '../ui';

interface Props { call: Call; categories: Category[]; onSaved: () => void; isNew?: boolean; onCancel?: () => void; }

// Account details (school/student/status/course/contract date) are DERIVED from JCSA at read time
// and shown read-only — only the account link, categories, notes, and status are edited.
interface Derived { SchoolName?: string; StudentName?: string; StudentStatus?: string; CourseName?: string; ContractDate?: string; }

export const CallEditForm: React.FC<Props> = ({ call, categories, onSaved, isNew = false, onCancel }) => {
  // Unique per instance so labels associate correctly even when several forms render at once.
  const uid = useId();
  const fid = (name: string) => `${uid}-${name}`;
  const [callerNumber, setCallerNumber] = useState(call.CallerNumber ?? '');
  const [accountNumber, setAccountNumber] = useState(call.AccountNumber ?? '');
  const [matchSource, setMatchSource] = useState(call.MatchSource ?? 'Manual');
  const [derived, setDerived] = useState<Derived>({
    SchoolName: call.SchoolName, StudentName: call.StudentName,
    StudentStatus: call.StudentStatus, CourseName: call.CourseName,
    ContractDate: call.ContractDate,
  });
  const [notes, setNotes] = useState(call.Notes ?? '');
  const [selectedCats, setSelectedCats] = useState<number[]>(call.Categories.map(c => c.CategoryID));
  const [candidates, setCandidates] = useState<AccountMatch[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [accountError, setAccountError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // The external party we phone-match on: caller for inbound, callee for outbound.
  const externalNumber = call.Direction === 'inbound' ? call.CallerNumber : call.CalleeNumber;

  // When the call has no chosen account yet but we have a number, surface the
  // candidate accounts so the agent can pick (covers the MultipleMatches case).
  useEffect(() => {
    if (accountNumber || !externalNumber) return;
    setCandidatesLoading(true);
    lookupByPhone(externalNumber)
      .then(rs => setCandidates(rs ?? []))
      .finally(() => setCandidatesLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Link an account and refresh the read-only derived display from the match.
  const applyMatch = (m: AccountMatch, source: string) => {
    setAccountNumber(m.AccountNumber);
    setDerived({ SchoolName: m.SchoolName, StudentName: m.StudentName, StudentStatus: m.StudentStatus, CourseName: m.CourseName, ContractDate: m.ContractDate });
    setMatchSource(source);
    setAccountError(null);
    setCandidates([]);
  };

  // Verify a typed account number exists; surface a message and clear the derived
  // display when it doesn't so the agent knows the number didn't match anything.
  const onAccountBlur = async () => {
    if (!accountNumber) { setDerived({}); setAccountError(null); return; }
    const match = await lookupAccount(accountNumber);
    if (match) applyMatch(match, 'Manual');
    else { setDerived({}); setAccountError(`Account ${accountNumber} was not found.`); }
  };

  const toggleCat = (id: number) =>
    setSelectedCats(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const save = async () => {
    setSaving(true);
    // Gate the save on the account number existing. If it doesn't, surface the error,
    // save nothing, and leave the record open so the agent can fix it.
    if (accountNumber) {
      const match = await lookupAccount(accountNumber);
      if (!match) {
        setDerived({});
        setAccountError(`Account ${accountNumber} was not found.`);
        setSaving(false);
        return;
      }
    }
    const input: CallInput = {
      AccountNumber: accountNumber || undefined,
      Notes: notes, MatchSource: matchSource,
      Status: 'Completed', CategoryIds: selectedCats,
    };
    // A draft only hits the DB here, on Save — clicking "Add Call" no longer persists anything.
    if (isNew) {
      input.CallerNumber = callerNumber || undefined;
      input.CallTimeUtc = new Date().toISOString();
      await createCall(input);
    } else {
      await updateCall(call.CallID, input);
    }
    setSaving(false);
    onSaved();
  };

  // A derived, read-only field. Deliberately NOT styled like an input (no border/box) —
  // just a label over plain value text — so it's obvious these can't be edited.
  const Ro: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
    <div>
      <span className="block text-xs font-medium uppercase tracking-wide text-muted">{label}</span>
      <div className="py-1.5 text-sm font-medium text-primary">
        {value || <span className="font-normal text-muted">—</span>}
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {!accountNumber && candidatesLoading && (
        <div className="rounded-md border border-border-default bg-surface-alt p-3">
          <LoadingSpinner size="sm" message={`Finding accounts matching ${externalNumber}…`} />
        </div>
      )}
      {!accountNumber && !candidatesLoading && candidates.length > 0 && (
        <div className="rounded-md border border-border-default bg-surface-alt p-3">
          <p className="mb-2 text-sm text-secondary">
            {candidates.length} accounts match {externalNumber} — pick one:
          </p>
          <div className={ui.tableWrap}>
            <table className={ui.table}>
              <thead className={ui.thead}>
                <tr>
                  <th className={ui.th} /><th className={ui.th}>Account #</th><th className={ui.th}>Student</th>
                  <th className={ui.th}>Center</th><th className={ui.th}>Signer</th><th className={ui.th}>Course</th>
                  <th className={ui.th}>Status</th><th className={ui.th}>Contract</th><th className={ui.th}>Matched on</th>
                </tr>
              </thead>
              <tbody className={ui.tbody}>
                {candidates.map(m => (
                  <tr key={m.AccountNumber} className="hover:bg-surface-hover">
                    <td className={ui.td}><button className={ui.btnSecondary} onClick={() => applyMatch(m, 'Phone')}>Use</button></td>
                    <td className={ui.td}>{m.AccountNumber}</td><td className={ui.td}>{m.StudentName}</td><td className={ui.td}>{m.SchoolName}</td>
                    <td className={ui.td}>{m.SignerName}</td><td className={ui.td}>{m.CourseName}</td><td className={ui.td}>{m.StudentStatus}</td>
                    <td className={ui.td}>{m.ContractDate ? new Date(m.ContractDate).toLocaleDateString() : ''}</td>
                    <td className={ui.td}>{m.MatchedOn}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {isNew && (
          <div>
            <label className={ui.label} htmlFor={fid('caller')}>Caller #</label>
            <input id={fid('caller')} className={ui.input} value={callerNumber} onChange={e => setCallerNumber(e.target.value)} />
          </div>
        )}
        <div>
          <label className={ui.label} htmlFor={fid('account')}>Account #</label>
          <input
            id={fid('account')}
            className={`${ui.input} ${accountError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
            value={accountNumber}
            onChange={e => { setAccountNumber(e.target.value); setAccountError(null); }}
            onBlur={onAccountBlur}
            aria-invalid={!!accountError}
          />
          {accountError && <p className="mt-1 text-xs text-red-600">{accountError}</p>}
        </div>
        <Ro label="Center / School" value={derived.SchoolName} />
        <Ro label="Student" value={derived.StudentName} />
        <Ro label="Course" value={derived.CourseName} />
        <Ro label="Status" value={derived.StudentStatus} />
        <Ro label="Contract Date" value={derived.ContractDate ? new Date(derived.ContractDate).toLocaleDateString() : undefined} />
      </div>

      <fieldset>
        <legend className="mb-2 text-sm font-medium text-secondary">Categories</legend>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map(c => (
            <label key={c.CategoryID} className="flex items-center gap-2 text-sm text-secondary">
              <input type="checkbox" className={ui.checkbox} checked={selectedCats.includes(c.CategoryID)} onChange={() => toggleCat(c.CategoryID)} />
              <span>{c.Code} {c.Name}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div>
        <label className={ui.label} htmlFor={fid('notes')}>Notes</label>
        <textarea id={fid('notes')} className={`${ui.input} min-h-[80px]`} value={notes} onChange={e => setNotes(e.target.value)} />
      </div>

      <div className="flex justify-end gap-2">
        {onCancel && (
          <button className={ui.btnSecondary} onClick={onCancel} disabled={saving}>Cancel</button>
        )}
        <button className={ui.btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </div>
  );
};
